/**
 * ChildSafetyPage.js — public Child Safety Standards (Google Play CSAE
 * compliance requirement for apps with chat/social features used by teens).
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, Siren, Eye, UserCheck, Mail, Scale } from 'lucide-react';
import PageMeta from '../components/PageMeta';

const Section = ({ icon: Icon, title, children }) => (
  <section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-4">
    <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-2">
      <Icon size={18} className="text-emerald-600" /> {title}
    </h2>
    <div className="text-sm text-gray-600 leading-relaxed space-y-2">{children}</div>
  </section>
);

export default function ChildSafetyPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <PageMeta
        title="Child Safety at HiBFF — Safeguarding Standards & CSAE Policy"
        description="How HiBFF protects young people: parental consent for every teen account, an always-on safety layer, human escalation, and strict child-safety standards."
        path="/child-safety"
        slug="child-safety"
      />
      <div className="max-w-3xl mx-auto px-4 py-10" data-testid="child-safety-page">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-3" data-testid="child-safety-back">
          <ArrowLeft size={14} /> Back to HiBFF
        </Link>
        <h1 className="text-3xl font-extrabold text-gray-900 mb-1">Child Safety Standards</h1>
        <p className="text-sm text-gray-500 mb-8">HIBFF LTD · Published standards against child sexual abuse and exploitation (CSAE) · Last updated 28 August 2026</p>

        <Section icon={ShieldCheck} title="Our commitment">
          <p>
            HiBFF exists to build teenagers&apos; real-world confidence safely. We maintain a
            zero-tolerance policy towards child sexual abuse and exploitation (CSAE) in any form.
            Content, behaviour or communication that sexualises, exploits, grooms or endangers a
            minor is prohibited, is actively detected, and is escalated — including to law
            enforcement where required.
          </p>
        </Section>

        <Section icon={Eye} title="Prevention & moderation built into the product">
          <p>• Every conversation on HiBFF passes through an always-on safety pipeline: strict content guardrails plus a continuous emotional-wellbeing monitor.</p>
          <p>• Prohibited content is blocked before it is displayed, and safety events are logged for human review in our Command Centre.</p>
          <p>• Teen accounts require verified parent or guardian details at registration, and parents co-sign a Family Charter before key features unlock.</p>
          <p>• There is no teen-to-stranger open chat: conversations are with HiBFF&apos;s moderated AI companions, with parent visibility by design.</p>
        </Section>

        <Section icon={Siren} title="In-app reporting & crisis support">
          <p>
            Every teen screen carries the red <strong>Help Shield</strong> — one tap opens immediate
            help, including crisis resources and the option to alert a trusted adult. Distress or
            risk signals detected by our monitoring trigger a human <strong>handover</strong> with
            parent/guardian notification. Users and parents can also report any concern via the
            in-app Help &amp; Support messaging, reviewed by our team.
          </p>
        </Section>

        <Section icon={Scale} title="Compliance with law & reporting to authorities">
          <p>
            We comply with applicable child-safety laws, including the UK Online Safety Act and
            relevant international standards. Confirmed CSAE material or credible risk to a child is
            reported to the appropriate authorities (in the UK: CEOP / the Internet Watch Foundation;
            elsewhere: the relevant national body such as NCMEC in the US) and we preserve evidence
            as legally required.
          </p>
        </Section>

        <Section icon={UserCheck} title="Designated child-safety contact">
          <p>
            Our named child-safety point of contact can be reached at{' '}
            <a href="mailto:safety@hibff.com" className="text-emerald-700 font-semibold underline" data-testid="child-safety-contact">
              safety@hibff.com
            </a>. Reports are acknowledged promptly and handled with priority by HIBFF LTD.
          </p>
        </Section>

        <Section icon={Mail} title="Related policies">
          <p>
            See also our <a href="/privacy" className="text-emerald-700 underline">Privacy &amp; Data Safety Policy</a>{' '}
            and <a href="/terms" className="text-emerald-700 underline">Terms &amp; Conditions</a>.
          </p>
        </Section>
      </div>
    </div>
  );
}
